import React, { useEffect, useState } from "react"
import Card from "../../../../components/atoms/Card.atom"
import { ValueWithLabelTemplate } from "../../../../components/layouts/containers/ValueWithLabelElements.container"
import { getClientMembersSummary } from "../../../../services/clientReport.service"
import InputSwitchForm from "../../../../components/molecules/InputSwitchForm.molecule"
import DataTable from "../../../../components/atoms/table/DataTable.atom"
import Column from "../../../../components/atoms/table/Column.atom"
import { CLIENT_SPRINT_CLOSURE_TYPES } from "../../../../consts/modules/clients.consts"
import {
  CLOSE_TYPE_LABELS,
  CLOSE_TYPES,
} from "../../../../consts/modules/clientReport.consts"
import useToast from "../../../../hooks/useToast.hook"
import { toastMessages } from "../../../../consts/common.const"
import { SPRINT_FILTER_PROPS } from "./SprintsFilter.controller"
import useReportFiltersContext from "../../../../hooks/contextConsumers/useReportFiltersContext.hook"
import { formatClientsHistoryValues } from "../../../../utils/clientReport.utils"
import IconWithTooltip from "../../../../components/molecules/IconWithTooltip.molecule"

const ClientTeamMembers = ({ selectedPeriod }) => {
  const { clientReportSelectedClient } = useReportFiltersContext()
  const { setErrorMessage } = useToast()
  const [membersSummary, setMembersSummary] = useState(null)
  const [loading, setLoading] = useState(true)
  const [tableData, setTableData] = useState([])
  const [showInactiveDevs, setShowInactiveDevs] = useState(false)
  const [devsRegistry, setDevsRegistry] = useState([])

  const hasNoData =
    !loading &&
    (!membersSummary ||
      membersSummary?.averageCompletedPointsPerTeamMember === null ||
      membersSummary?.averageCompletedPointsPerTeamMember === undefined)

  useEffect(() => {
    if (!clientReportSelectedClient) {
      setLoading(false)
      return
    }

    setLoading(true)

    getClientMembersSummary({ clientId: clientReportSelectedClient })
      .then((res) => {
        if (res.success) {
          setMembersSummary(res.result.sprintsSummary)
          setTableData(res.result.tableData)
          setDevsRegistry(res.result.devsRegistry)
        } else {
          setMembersSummary(null)
          setTableData([])
          setDevsRegistry([])
          setErrorMessage({ message: toastMessages.dataDoesNotLoad.details })
        }
      })
      .catch(() => {
        setErrorMessage({ message: toastMessages.dataDoesNotLoad.details })
        setMembersSummary(null)
        setTableData([])
        setDevsRegistry([])
      })
      .finally(() => {
        setLoading(false)
      })
  }, [clientReportSelectedClient])

  const visibleDevs =
    showInactiveDevs ? devsRegistry : devsRegistry.filter((dev) => dev.isActive)

  const validSprintsForCalculations = tableData.filter(
    (sprint) => sprint.closeType !== CLIENT_SPRINT_CLOSURE_TYPES.timeout
  )

  const totals = validSprintsForCalculations.reduce(
    (acc, curr) => {
      acc.pointsMade += Number(curr.pointsMade) || 0
      acc.avgDevPoints += Number(curr.avgDevPoints) || 0
      devsRegistry.forEach((dev) => {
        const name = dev.nameTag
        const devValue = curr[name]
        const hasParticipation =
          devValue !== null && devValue !== undefined && devValue !== "-"

        if (!hasParticipation) return

        acc.devs[name] = (acc.devs[name] || 0) + (Number(devValue) || 0)
        acc.devParticipationCount[name] =
          (acc.devParticipationCount[name] || 0) + 1
      })

      return acc
    },
    { pointsMade: 0, avgDevPoints: 0, devs: {}, devParticipationCount: {} }
  )

  const generalInfoTemplate = (value, tooltipContent, unit) => {
    if (hasNoData) return "-"
    const val = formatClientsHistoryValues(value).toString()
    return (
      <div className="flex align-items-center gap-3">
        <div>
          {val} <span>{unit}</span>
        </div>
        <IconWithTooltip
          tooltipValue={tooltipContent}
          iconClassName="pi pi-info-circle"
        />
      </div>
    )
  }

  return (
    <>
      <div className="client-history-summary-container">
        <Card>
          <div className="flex flex-column font-cairo">
            <p style={{ fontSize: "18px", marginBottom: 7 }}>
              Información general
            </p>
            <div className="flex flex-wrap gap-3">
              <ValueWithLabelTemplate
                label="Puntos por participante promedio"
                value={() =>
                  generalInfoTemplate(
                    membersSummary?.averageCompletedPointsPerTeamMember,
                    "Promedio de puntos realizados por los desarrolladores en todos los sprints del proyecto",
                    "pts"
                  )
                }
                loading={loading}
              />
              <ValueWithLabelTemplate
                label="Horas por punto promedio"
                value={() =>
                  generalInfoTemplate(
                    membersSummary?.averageHoursPerPoint,
                    "Promedio de horas necesarias para realizar un punto del proyecto",
                    "hrs"
                  )
                }
                loading={loading}
              />
              <ValueWithLabelTemplate
                label="Promedio de horas dev p/sprint"
                value={() =>
                  generalInfoTemplate(
                    membersSummary?.averageDevHoursPerSprint,
                    "Promedio de horas realizadas por los desarrolladores en un sprint",
                    "hrs"
                  )
                }
                loading={loading}
              />
              <ValueWithLabelTemplate
                label="Participantes promedio"
                value={() =>
                  generalInfoTemplate(
                    membersSummary?.averageTeamMemberPerSprint,
                    "Número de desarrolladores promedio por sprint en el proyecto",
                    ""
                  )
                }
                loading={loading}
              />
            </div>
          </div>
        </Card>
      </div>
      <Card className="mt-3 client-historic-card-container">
        <div className="flex w-full justify-content-between align-items-center mb-4">
          <span style={{ fontSize: "18px" }}>Consumo histórico por sprint</span>
          <InputSwitchForm
            label="Mostrar desarrolladores inactivos"
            value={showInactiveDevs}
            onChange={() => setShowInactiveDevs(!showInactiveDevs)}
            labelPosition="left"
          />
        </div>
        <DataTable
          value={tableData}
          scrollable
          className="history-table"
          emptyMessageContent="No hay información de los sprints"
          emptyMessageTitle="Nada por aquí (todavía)"
          loading={loading}
        >
          <Column
            {...SPRINT_FILTER_PROPS}
            header="Sprint"
            footer="Totales"
            field="sprintNumberByClient"
            body={(rowData) => {
              const currSprint = selectedPeriod.sprintNumber
              return (
                <span
                  className={
                    currSprint === rowData.sprintNumberByClient ?
                      "highlighted-task-column"
                    : ""
                  }
                >
                  {rowData.closeType === CLOSE_TYPES.TIMEOUT ?
                    CLOSE_TYPE_LABELS.TIMEOUT
                  : rowData.sprintNumberByClient}
                </span>
              )
            }}
          />
          <Column header="Desarrolladores" field="totalDevs" />
          <Column
            header="Pts realizados"
            field="pointsMade"
            footer={() => {
              if (tableData.length === 0) return "-"
              return `${totals.pointsMade} pts`
            }}
          />
          <Column
            header={
              <div className="flex gap-3 align-items-center">
                <span>Pts por dev</span>
                <IconWithTooltip
                  tooltipValue={
                    "Los puntos por desarrollador pueden incluir también desarrolladores inactivos"
                  }
                  iconClassName="pi pi-info-circle"
                />
              </div>
            }
            field="avgDevPoints"
            footer={() => {
              if (tableData.length === 0) return "-"
              const avg =
                totals.avgDevPoints / validSprintsForCalculations.length
              return `${formatClientsHistoryValues(avg).toString()} pts`
            }}
          />
          {visibleDevs.map((dev) => {
            const columnClass = dev.isActive ? "" : "inactive-dev"
            return (
              <Column
                key={dev.nameTag}
                header={dev.nameTag}
                headerClassName={columnClass}
                className={columnClass}
                field={dev.nameTag}
                body={(rowData) => rowData[dev.nameTag] ?? "-"}
                footer={() => {
                  if (tableData.length === 0) return "-"
                  const participationCount =
                    totals.devParticipationCount[dev.nameTag] || 0
                  if (participationCount === 0) return "-"
                  const avgDevPoints =
                    (totals.devs[dev.nameTag] || 0) / participationCount
                  return `${formatClientsHistoryValues(avgDevPoints).toString()} pts`
                }}
              />
            )
          })}
        </DataTable>
      </Card>
    </>
  )
}

export default ClientTeamMembers
